import * as React from 'react'
import styled from 'styled-components'
import I18n from '../../core/i18n'
import { postStartTimeService } from '../../core/services'
import * as constants from '../../static/constants'
import * as interfaces from '../../static/interfaces'
import { Button, Select } from '../atoms'
import { Form } from '../molecules'
import SvgIcon from 'components/atoms/SvgIcon'

import * as moment from 'moment'
import { DateRangePicker } from 'react-dates'
import 'react-dates/initialize'

declare var window: interfaces.IWindow

const POST_START_TIME_FIELDS = {
  start_date: 'start_date',
  end_date: 'end_date',
  start_time: 'start_time',
  days: 'days',
}

interface IErrors {
  [key: string]: string | null
}

interface IStartTime {
  id?: number
  start_date: string
  end_date: string
  start_time: string
  days: number[]
}

interface IProps {
  post: interfaces.IPost
  startTimes: IStartTime[]
  setStartTimes: (startTime: any) => void
}

const startTimeInitialValues = {
  id: null,
  start_date: '',
  end_date: '',
  start_time: '',
  days: [],
}

const formatMeetingPoint = ({ id, start_date, end_date, start_time, days }) => ({
  id,
  start_date,
  end_date,
  start_time,
  days,
})

export const PostStartTime: React.FC<IProps> = props => {
  const [editStartTime, setEditStartTime] = React.useState(null)
  const [isSubmitEnabled, setIsSubmitEnabled] = React.useState(false)
  const [errors, setErrors] = React.useState<IErrors>({})
  const isMobile = matchMedia(`(max-width: ${constants.BREAKPOINT_TABLET_SMALL}px)`).matches
  const handleSubmit = async (initialValues, values) => {
    if (
      !!!moment(values.start_date).isValid() ||
      !!!moment(values.end_date).isValid() ||
      moment(values.start_date).format('YYYYMMDD').length !== 8 ||
      moment(values.end_date).format('YYYYMMDD').length !== 8
    ) {
      setErrors({ date: 'Plese select date' })
      return
    }
    const params = {
      ...values,
      end_date: moment(values.end_date).format('YYYY-MM-DD'),
      start_date: moment(values.start_date).format('YYYY-MM-DD'),
      days: values.days.map(day => Number(day)),
      post_id: props.post.id,
    }

    if (editStartTime.id) {
      const { startTime: updated, flush } = await postStartTimeService.update(
        editStartTime.id,
        params
      )
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
      props.setStartTimes(current =>
        current.map(item => (item.id === updated.id ? formatMeetingPoint(updated) : item))
      )
      handleBackToIndex()
    } else {
      const { startTime: created, flush } = await postStartTimeService.create(params)
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
      props.setStartTimes(current => [...current, formatMeetingPoint(created)])
      handleBackToIndex()
    }
  }

  const handleDelete = async id => {
    const { flush } = await postStartTimeService.delete(id)
    window.flashMessages.addMessage({ text: flush.message, type: flush.type })
    props.setStartTimes(current => current.filter(item => item.id !== id))
  }

  const handleEdit = startTime => {
    setEditStartTime(startTime)
  }

  const handleCreate = startTime => {
    setEditStartTime(startTime)
  }

  const handleBackToIndex = () => {
    setEditStartTime(null)
    setErrors({})
  }

  const handleUpdateForm = (updatedErrors, updatedIsSubmitEnabled) => {
    setErrors(updatedErrors)
    setIsSubmitEnabled(updatedIsSubmitEnabled)
  }

  // Date range picker
  const [selectedDate, setSelectedDate] = React.useState({ start: null, end: null })
  const [focusedInput, setFocusedInput] = React.useState(null)

  const changeFocusedInput = (changedFocusedInput: any) => {
    setFocusedInput(changedFocusedInput)
  }

  const onDateRangePickerChange = (dates: {
    startDate: moment.Moment | null
    endDate: moment.Moment | null
  }) => {
    setSelectedDate({
      start: dates.startDate,
      end: dates.endDate,
    })
  }

  React.useEffect(() => {
    if (editStartTime?.start_date && editStartTime?.end_date) {
      setSelectedDate({
        start: moment(editStartTime?.start_date),
        end: moment(editStartTime?.end_date),
      })
    } else {
      setSelectedDate({
        start: null,
        end: null,
      })
    }
  }, [editStartTime])

  return (
    <Wrapper>
      <h2 className="Editor_Title">{I18n.t('generic.start_time')}</h2>

      {editStartTime && (
        <Form
          fields={POST_START_TIME_FIELDS}
          handleUpdateForm={handleUpdateForm}
          handleSubmit={handleSubmit}
        >
          <div className="FormItmes">
            <ul>
              <FormItem>
                <DateRangeInput>
                  <p className="DateRangeInput_Label">Date</p>
                  <DateRangePicker
                    startDate={selectedDate.start}
                    startDateId="start_date"
                    endDate={selectedDate.end}
                    endDateId="end_date"
                    onDatesChange={onDateRangePickerChange}
                    focusedInput={focusedInput}
                    onFocusChange={(currentFocusedInput: string | null) =>
                      changeFocusedInput(currentFocusedInput)
                    }
                    numberOfMonths={isMobile ? 1 : 2}
                    enableOutsideDays={false}
                    hideKeyboardShortcutsPanel={true}
                    displayFormat={constants.DATE_FORMAT}
                  />
                </DateRangeInput>
                {errors.date && <span className="Date_Error">{errors.date}</span>}
              </FormItem>
              <FormItem>
                <Select
                  name="start_time"
                  label="Start time"
                  options={constants.START_TIMES.map(time => ({ label: time, value: time }))}
                  defaultValue={editStartTime?.start_time || '12:00'}
                  error={errors.start_time}
                />
              </FormItem>
              <FormItem>
                <CheckBox>
                  <p className="CheckBox_Title">{I18n.t('generic.tag')}</p>
                  <div className="CheckBox_Main">
                    {constants.DAYS.map(day => (
                      <label key={day.value}>
                        <input
                          type="checkbox"
                          name="days"
                          value={String(day.value)}
                          defaultChecked={editStartTime.days.includes(day.value)}
                        />
                        <span className="CheckBox_Icon">
                          <SvgIcon
                            name="check_box"
                            className="icon filled"
                            ariaHidden={true}
                            role="presentation"
                          />
                          <SvgIcon name="check_box_outline_blank" className="icon outline" />
                        </span>
                        <span>{day.name}</span>
                      </label>
                    ))}
                  </div>
                </CheckBox>
              </FormItem>
            </ul>
          </div>
          <div className="Editor_Footer">
            <Button handleClick={() => handleBackToIndex()}>
              {I18n.t('generic.back_to_index')}
            </Button>
            <Button primary={true}>
              {editStartTime.id ? I18n.t('generic.update') : I18n.t('generic.create')}
            </Button>
          </div>
        </Form>
      )}

      {editStartTime === null && (
        <StartTimeList>
          <ul>
            {props.startTimes.map(startTime => (
              <li key={startTime.id}>
                <div className="values">
                  <div className="">
                    {startTime.start_date} ~ {startTime.end_date}
                  </div>
                  <div className="">{startTime.start_time}</div>
                  <div className="">
                    {constants.DAYS.filter(({ value }) => startTime.days.includes(value))
                      .map(({ name }) => name)
                      .join(', ')}
                  </div>
                </div>
                <div>
                  <Button primary={true} handleClick={() => handleEdit({ ...startTime })}>
                    {I18n.t('generic.edit')}
                  </Button>
                  <Button handleClick={() => handleDelete(startTime.id)}>
                    {I18n.t('generic.delete')}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
          <div className="buttons">
            <Button primary={true} handleClick={() => handleCreate({ ...startTimeInitialValues })}>
              {I18n.t('generic.create_new')}
            </Button>
          </div>
        </StartTimeList>
      )}
    </Wrapper>
  )
}

const DateRangeInput = styled.div`
  display: flex;

  @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
    flex-direction: column;
  }

  .DateRangeInput_Label {
    width: 240px;
    margin-top: 8px;
    font-size: 15px;
    font-weight: bold;
    line-height: 1;

    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      width: 100%;
      margin-bottom: 12px;
    }
  }
`

const StartTimeList = styled.ul`
  padding: 20px;

  li {
    padding: 8px 0;

    > div + div {
      margin-top: 10px;
    }
  }

  li + li {
    border-top: solid 1px ${constants.COLORS.divider};
  }

  .values {
    display: flex;
    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      flex-direction: column;
      align-items: end;
      margin-bottom: 10px;
    }

    > div + div {
      margin-left: 24px;
      @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
        margin-left: 0px;
      }
    }
  }

  .buttons {
    margin-top: 24px;
  }
`

const Wrapper = styled.div`
  .map {
    height: 320px;
    margin-top: 32px;
  }
`

const FormItem = styled.li`
  & + & {
    margin-top: 16px;
  }

  .FormItem_Title {
    font-size: 15px;
    font-weight: bold;
  }

  .Date_Error {
    display: block;
    margin-top: 8px;
    color: ${constants.COLORS.cautionText};
    font-size: 14px;
    margin-left: 240px;
    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      margin-left: 0px;
    }
  }
`

const CheckBox = styled.div`
  display: flex;

  @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
    flex-direction: column;
  }

  .CheckBox_Title {
    width: 240px;
    margin-top: 8px;
    font-size: 15px;
    font-weight: bold;
    line-height: 1;

    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      width: 100%;
      margin-bottom: 12px;
    }
  }

  .CheckBox_Main {
    flex: 1;

    > label {
      display: block;
      position: relative;
      cursor: pointer;

      &:hover {
        opacity: 0.75;
      }

      input {
        opacity: 0;
        height: 0;
        width: 0;
      }

      .CheckBox_Icon {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        margin: auto;
        width: 24px;
        height: 24px;

        .icon {
          position: absolute;
          top: 0;
          left: 0;

          &.filled {
            display: none;
            color: ${constants.COLORS.primary};
          }

          &.outline {
            display: block;
            color: ${constants.COLORS.divider};
          }
        }
      }

      input:checked + .CheckBox_Icon {
        .icon {
          &.filled {
            display: block;
          }

          &.outline {
            display: none;
          }
        }
      }

      span {
        font-size: 14px;
        padding-left: 28px;
      }
    }

    > label + label {
      margin-top: 16px;
    }
  }
`

export default PostStartTime
